import { ValidatorSchema, Validator } from '@libs/utilities/validator';


/**------------------------------------------------------
 * Enum Definitions
 */
export enum EnumLegalInjectionToken {
	Config = 'legal_config'
}

export enum EnumLegalPageRoute {
	Imprint				= 'imprint',
	CookiesUsage 		= 'cookies-usage',
	PrivacyPolicy 		= 'privacy-policy',
	CancellationPolicy 	= 'cancellation-policy',
	TermsOfUse 			= 'terms-of-use',
	AffiliateTerms 		= 'affiliate-terms'
}


/**------------------------------------------------------
 * Interface Definitions
 */
export interface ILegalPagesConfig {
	language			: string;
	company				: ILegalCompany;
	disabledPages 		: EnumLegalPageRoute[];
	payment				: ILegalPayment;
	engagement			: ILegalEngagement;
	addons				: ILegalAddons;
	termsOfUse			: ILegalTermsOfUse;
	cancellationPolicy	: ILegalCancellationPolicy;
	affiliateTerms	   ?: ILegalAffiliateTerms;
	/** Complete Markdown documents supplied by a product, in their authored language. */
	pageContent		   ?: Partial<Record<EnumLegalPageRoute, string>>;
}

interface ILegalCompany {
	address				: string[];
	phone				: string;
	mail				: string;
	directors			: string[];
	court				: string;
	courtNumber			: string;
	vat					: string;
	legal				: {
		localCourt		: string;
		lawCountry		: string;
	};
}

interface ILegalPayment {
	providers			: {
		paddle			: boolean;
		stripe			: boolean;
		paypal			: boolean;
	};
	processing			: {
		recurringPayments: boolean;
		inAppPurchases	: boolean;
	};
	affiliation			: {
		tapfiliate		: boolean;
	};
}

interface ILegalEngagement {
	community			: {
		facebook		: boolean;
		discord			: boolean;
	};
	documentation		: boolean;
	customerSupport		: boolean;
}

interface ILegalAddons {
	newsletter			: {
		mailChimp		: boolean;
	};
	plugins				: {
		youTube     	: boolean;
		reCaptcha   	: boolean;
		facebookChat	: boolean;
	};
	socialLogin			: {
		facebook    	: boolean;
		google      	: boolean;
		apple			: boolean;
	};
	analytics			: {
		googleAnalytics	: boolean;
		facebookPixel	: boolean;
	};
}

interface ILegalTermsOfUse {
	products			: {
		userAccount		: boolean;
		freeVersionSAAS	: boolean;
		subscriptionSAAS: boolean;
	};
	productTerms		: ILegalCustomTextSection[];
}

interface ILegalCancellationPolicy {
	selfCancellation   ?: ILegalCustomTextSection;
	refundPolicies	   ?: ILegalCustomTextSection[];
}

interface ILegalCustomTextSection {
	heading				: string;
	texts				: string[];
}

interface ILegalAffiliateTerms {
	// ... add customizations when needed
}


/**------------------------------------------------------
 * Schema
 */
class LegalCustomTextSectionSchema implements ValidatorSchema<ILegalCustomTextSection> {
	heading	= Validator.String().isNotEmpty();
	texts	= Validator.Array().isNotEmpty().each(Validator.String());
}

class LegalPageContentSchema implements ValidatorSchema<Partial<Record<EnumLegalPageRoute, string>>> {
	[EnumLegalPageRoute.CookiesUsage] = Validator.String().optional().isNotEmpty();
	[EnumLegalPageRoute.PrivacyPolicy] = Validator.String().optional().isNotEmpty();
	[EnumLegalPageRoute.CancellationPolicy] = Validator.String().optional().isNotEmpty();
	[EnumLegalPageRoute.TermsOfUse] = Validator.String().optional().isNotEmpty();
	[EnumLegalPageRoute.AffiliateTerms] = Validator.String().optional().isNotEmpty();
	[EnumLegalPageRoute.Imprint] = Validator.String().optional().isNotEmpty();
}

export class LegalPagesConfigSchema implements ValidatorSchema<ILegalPagesConfig> {
	language			= Validator.String().isNotEmpty();
	pageContent			= Validator.Object().optional().inspect<Partial<Record<EnumLegalPageRoute, string>>>(LegalPageContentSchema);
	company				= {
		phone				: Validator.String().isNotEmpty(),
		address				: Validator.Array().isNotEmpty().each(Validator.String()),
		mail				: Validator.String().isNotEmpty(),
		directors			: Validator.Array().isNotEmpty().each(Validator.String()),
		court				: Validator.String().isNotEmpty(),
		courtNumber			: Validator.String().isNotEmpty(),
		vat					: Validator.String().isNotEmpty(),
		legal				: {
			localCourt		: Validator.String().isNotEmpty(),
			lawCountry		: Validator.String().isNotEmpty()
		}
	};
	disabledPages 		= Validator.Array().optional().each(Validator.Enum(EnumLegalPageRoute));
	payment				= {
		providers			: {
			paddle			: Validator.Boolean(),
			stripe			: Validator.Boolean(),
			paypal			: Validator.Boolean()
		},
		processing			: {
			recurringPayments: Validator.Boolean(),
			inAppPurchases	: Validator.Boolean()
		},
		affiliation			: {
			tapfiliate		: Validator.Boolean()
		}
	};
	engagement			= {
		community			: {
			facebook		: Validator.Boolean(),
			discord			: Validator.Boolean()
		},
		documentation		: Validator.Boolean(),
		customerSupport		: Validator.Boolean()
	};
	addons				= {
		newsletter			: {
			mailChimp		: Validator.Boolean()
		},
		plugins				: {
			youTube     	: Validator.Boolean(),
			reCaptcha   	: Validator.Boolean(),
			facebookChat	: Validator.Boolean()
		},
		socialLogin			: {
			facebook    	: Validator.Boolean(),
			google      	: Validator.Boolean(),
			apple			: Validator.Boolean()
		},
		analytics			: {
			googleAnalytics	: Validator.Boolean(),
			facebookPixel	: Validator.Boolean()
		}
	};
	termsOfUse			= {
		products			: {
			userAccount		: Validator.Boolean(),
			freeVersionSAAS	: Validator.Boolean(),
			subscriptionSAAS: Validator.Boolean()
		},
		productTerms		: Validator.Array().inspectEach<ILegalCustomTextSection>(LegalCustomTextSectionSchema)
	};
	cancellationPolicy	= {
		selfCancellation	: Validator.Object().optional().inspect<ILegalCustomTextSection>(LegalCustomTextSectionSchema),
		refundPolicies		: Validator.Array().optional().inspectEach<ILegalCustomTextSection>(LegalCustomTextSectionSchema)
	};
	affiliateTerms		= Validator.Object().optional().inspect<ILegalAffiliateTerms>({
		// ... add customizations when needed
	});
}
